/**
 * Teams API client
 */

import { getAPIClient } from './client';

export interface InviteUserRequest {
  email: string;
  tenant_id?: string;
  project_id?: string;
  role_id?: string;
}

export interface Invitation {
  id: string;
  email: string;
  tenant_id?: string;
  project_id?: string;
  role_id?: string;
  token?: string; // Only shown on creation
  expires_at: string;
  status: string;
  created_at: string;
}

export interface ListInvitationsResponse {
  invitations: Invitation[];
  total: number;
}

export interface AcceptInvitationRequest {
  token: string;
}

export interface AcceptInvitationWithRegistrationRequest {
  token: string;
  username: string;
  password: string;
  full_name?: string;
}

export interface AcceptInvitationResponse {
  message: string;
  invitation_id: string;
  user_id?: string;
  tenant_id?: string;
  project_id?: string;
  role_name?: string;
  access_token?: string;  // For auto-login after registration
  refresh_token?: string;  // For auto-login after registration
  token_type?: string;
  expires_in?: number;
}

export interface InvitationDetails {
  email: string;
  tenant_id?: string;
  tenant_name?: string;
  project_id?: string;
  project_name?: string;
  role_id?: string;
  role_name?: string;
  inviter_name?: string;
  expires_at: string;
  status: string;
  is_expired: boolean;
  is_valid: boolean;
}

export interface TeamMember {
  user_id: string;
  email: string;
  username: string;
  full_name?: string;
  role_id?: string;
  role_name?: string;
  status: string;
  joined_at: string;
}

export interface ListTeamMembersResponse {
  members: TeamMember[];
  total: number;
}

export interface AssignRoleRequest {
  user_id: string;
  tenant_id?: string;
  project_id?: string;
  role_id?: string;
}

/**
 * Invite user to tenant/project
 */
export async function inviteUser(request: InviteUserRequest): Promise<Invitation> {
  const client = getAPIClient();
  return client.post<Invitation>('/teams/invitations', request);
}

/**
 * List invitations
 */
export async function listInvitations(params?: {
  tenant_id?: string;
  project_id?: string;
  status?: string;
}): Promise<ListInvitationsResponse> {
  const client = getAPIClient();
  return client.get<ListInvitationsResponse>('/teams/invitations', { params });
}

/**
 * Accept invitation (for authenticated users)
 */
export async function acceptInvitation(request: AcceptInvitationRequest): Promise<AcceptInvitationResponse> {
  const client = getAPIClient();
  return client.post<AcceptInvitationResponse>('/teams/invitations/accept', request);
}

/**
 * Accept invitation with registration (for new users)
 */
export async function acceptInvitationWithRegistration(request: AcceptInvitationWithRegistrationRequest): Promise<AcceptInvitationResponse> {
  const client = getAPIClient();
  return client.post<AcceptInvitationResponse>('/teams/invitations/accept-with-registration', request);
}

/**
 * Assign role to user
 */
export async function assignRole(request: AssignRoleRequest): Promise<{ message: string; user_id: string; tenant_id?: string; project_id?: string; role_id?: string }> {
  const client = getAPIClient();
  return client.post('/teams/assign-role', request);
}

/**
 * List team members
 */
export async function listTeamMembers(params: {
  tenant_id?: string;
  project_id?: string;
}): Promise<ListTeamMembersResponse> {
  const client = getAPIClient();
  return client.get<ListTeamMembersResponse>('/teams/members', { params });
}

/**
 * Get invitation details (public endpoint, no auth required)
 */
export async function getInvitationDetails(token: string): Promise<InvitationDetails> {
  // Use raw axios for public endpoint to avoid auth headers
  const axios = (await import('axios')).default;
  const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
  
  const response = await axios.get<InvitationDetails>(`${baseURL}/teams/invitations/details`, {
    params: { token },
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  return response.data;
}

export interface ResendInvitationRequest {
  invitation_id: string;
}

export interface ResendInvitationResponse {
  message: string;
  invitation_id: string;
  email: string;
  expires_at: string;
}

/**
 * Resend invitation email
 */
export async function resendInvitation(request: ResendInvitationRequest): Promise<ResendInvitationResponse> {
  const client = getAPIClient();
  return client.post<ResendInvitationResponse>('/teams/invitations/resend', request);
}











